import {
  Component,
  Input,
  signal,
  computed,
  effect,
  OnInit,
  WritableSignal,
  Signal,
} from '@angular/core';
import { ProductType } from 'src/app/core/models';
import { TranslateLabelPipe } from 'src/app/core/pipes/translate-label.pipe';
import { TranslateService } from '@ngx-translate/core';
import { SelectOption } from 'src/types';
import {
  DEFAULT_VALUE as ESTIMATE_DEFAULT_VALUE,
  LOCAL_STORAGE_KEY as ESTIMATE_STORAGE_KEY,
} from 'src/app/pages/settings/estimate-type/estimate-type.component';
import { SettingsService } from 'src/app/core/providers';
import { CommissionEstimateService } from '../services/commission-estimate.service';
import processCommissionEstimate from '../utils/process-commission-estimates';

import type {
  MetricTierConfiguration,
  EstimateConfiguration,
  PeriodMetric,
} from '../types';

const DEBUG = false;

const log = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

export function getSliderPosition(
  min: number,
  max: number,
  value: number
): number {
  const range = max - min;
  const percent = (value - min) / range;
  return percent;
}

// TODO: Should this be rounded?
export function calculateEstimate(value: number, percentage: number): number {
  const rawValue = value * (percentage / 100);
  return Math.round(rawValue);
}

export type ProductTypeItemLabels = {
  allProductsLabel: string;
  row1Label?: string;
  row2Label?: string;
  row3Label?: string;
  row4Label?: string;
  row5Label?: string;
  customerRowLabel?: string;
  rateTypeLabel?: string;
};

type MetricsContainer = {
  monthlyText: string;
  weeklyText: string;
  dailyText: string;
  hourlyText: string;
};

@Component({
  selector: 'app-product-type-item',
  templateUrl: './product-type-item.component.html',
  styleUrls: ['./product-type-item.component.scss'],
  providers: [TranslateLabelPipe, TranslateService],
  standalone: false,
})
export class ProductTypeItemComponent implements OnInit, MetricsContainer {
  public isEstimateOpen: WritableSignal<boolean> = signal(false);
  public selectedPeriod: WritableSignal<PeriodMetric> = signal('monthly');
  // TODO: Determine what the initial value of this should be. Right now it's just hard-coded for testing.
  readonly estimatePercentage: WritableSignal<number> = signal(3);
  readonly estimateBaseValue: WritableSignal<number> = signal(-1);
  readonly estimateComputedValue: WritableSignal<number> = signal(-1);
  readonly estimateMin: WritableSignal<number> = signal(-1);
  readonly estimateMax: WritableSignal<number> = signal(-1);
  readonly defaultEstimateValue: WritableSignal<number> = signal(-1);
  public estimateConfigurations: WritableSignal<EstimateConfiguration | null> =
    signal(null);
  // public tierConfigurations: WritableSignal<any> = signal(null);
  public isEstimateLoading: WritableSignal<boolean> = signal(false);
  public isEstimateLoaded: WritableSignal<boolean> = signal(false);
  public monthlyText: string;
  public weeklyText: string;
  public dailyText: string;
  public hourlyText: string;

  // private estimateTiers: Record<string, number> = {};
  readonly tiersPercentages: WritableSignal<number[]> = signal([]);

  @Input()
  productType: any;

  @Input()
  getLabelTranslation: any; // (str: string) => string;

  @Input()
  showHourly: boolean;

  @Input()
  showEstimate: boolean;

  @Input()
  openAssetAvailability: (productType: any) => unknown;

  @Input()
  selectedCustomerCount: () => number;

  @Input()
  estimateColors: string[] = [];

  @Input()
  labels: ProductTypeItemLabels = {
    allProductsLabel: 'All Products',
    row1Label: '',
    row2Label: '',
    row3Label: '',
    row4Label: '',
    row5Label: '',
    customerRowLabel: '',
    rateTypeLabel: '',
  };

  @Input()
  localeFormat: any;

  public columnOptions: SelectOption[] = [
    {
      label: 'Monthly',
      value: 'monthly',
    },
    {
      label: 'Weekly',
      value: 'weekly',
    },
    {
      label: 'Daily',
      value: 'daily',
    },
    {
      label: 'Hourly',
      value: 'hourly',
    },
  ];

  // This will provide the translated version of the selected period metric.
  public metricLabel: Signal<PeriodMetric> = computed(() => {
    const type: PeriodMetric = this.selectedPeriod();
    const result = (this as any as MetricsContainer)[
      `${type}Text`
    ] as PeriodMetric;
    return result;
  });

  constructor(
    public translateService: TranslateService,
    protected settingsService: SettingsService,
    protected commissionEstimateService: CommissionEstimateService
  ) {
    this.toggleOpenEstimate = this.toggleOpenEstimate.bind(this);
    this.onPeriodChange = this.onPeriodChange.bind(this);
    this.onEstimateChange = this.onEstimateChange.bind(this);
  }

  ngOnInit() {
    if (this.getLabelTranslation) {
      this.monthlyText = this.getLabelTranslation('Monthly');
      this.weeklyText = this.getLabelTranslation('Weekly');
      this.dailyText = this.getLabelTranslation('Daily');
      this.hourlyText = this.getLabelTranslation('Hourly');
      this.columnOptions[0].label = this.monthlyText;
      this.columnOptions[1].label = this.weeklyText;
      this.columnOptions[2].label = this.dailyText;
      this.columnOptions[3].label = this.hourlyText;
      if (!this.showHourly) {
        this.columnOptions.pop();
      }
    }

    if (DEBUG) {
      const keys = [
        'commission_hourly_min_rate',
        'commission_hourly_max_rate',
        'commission_daily_min_rate',
        'commission_daily_max_rate',
        'commission_weekly_min_rate',
        'commission_weekly_max_rate',
        'commission_monthly_min_rate',
        'commission_monthly_max_rate',
      ];
      keys.forEach((key) => {
        console.log(
          `product-type-item.component.ngOnInit  ${key}`,
          this.productType[key]
        );
      });
    }

    const periodValue: PeriodMetric =
      (this.settingsService.getStoredValue(ESTIMATE_STORAGE_KEY) as
        | PeriodMetric
        | undefined) || ESTIMATE_DEFAULT_VALUE;

    this.selectedPeriod.set(periodValue);
  }

  public onPeriodChange(value: any) {
    log('product-type-item.component.onPeriodChange  ', value);
    this.selectedPeriod.set(value);
    try {
      this.applyEstimateValues();
      this.setTiers();
    } catch (error) {
      if (error instanceof Error) {
        log('Error applying estimate configuration:', error.message);
      }
    }
  }

  public toggleOpenEstimate() {
    if (this.isEstimateOpen()) {
      this.isEstimateOpen.set(false);
      return;
    }
    // Don't reload data if it's already loaded.
    if (this.isEstimateLoaded()) {
      this.isEstimateOpen.set(true);
      return;
    }

    const data = this.productType;
    this.isEstimateLoading.set(true);
    (async () => {
      try {
        const result =
          await this.commissionEstimateService.getEstimateConfiguration(
            data.category_id,
            data.product_type_id
          );
        this.estimateConfigurations.set(processCommissionEstimate(result));
        this.isEstimateOpen.set(!this.isEstimateOpen());

        this.applyEstimateValues();
        this.setTiers();
      } catch (error) {
        if (error instanceof Error) {
          log('Error applying estimate configuration:', error.message);
        }
      } finally {
        this.isEstimateLoaded.set(true);
        this.isEstimateLoading.set(false);
      }
    })();
  }

  public onEstimateChange(value: number) {
    this.estimateBaseValue.set(value);
    try {
      this.applyEstimateValues();
    } catch (error) {
      if (error instanceof Error) {
        log('Error applying estimate configuration:', error.message);
      }
    }
  }

  private getPercentageValue(ratio: number) {
    const value =
      this.estimateMin() + (this.estimateMax() - this.estimateMin()) * ratio;
    return value;
  }

  private applyEstimateValues(): void {
    log(
      'product-type-item.component.applyEstimateValue  ',
      this.selectedPeriod()
    );

    const configs = this.estimateConfigurations();

    if (!configs) {
      throw new Error('No estimate configurations found');
    }

    const config = configs ? configs[this.selectedPeriod()] : null;

    if (!config) {
      throw new Error('No config found');
    }

    this.estimatePercentage.set(configs.client_commission_percent);

    const minValueProp = config.adjusted_min_rate;
    const minValue: number = !minValueProp ? -1 : minValueProp;
    this.estimateMin.set(Math.round(minValue));

    const maxValueProp = config.adjusted_max_rate;
    const maxValue: number = !maxValueProp ? -1 : maxValueProp;
    this.estimateMax.set(Math.round(maxValue));

    if (this.estimateBaseValue() === -1) {
      this.estimateBaseValue.set(this.estimateMin());
    }

    const currentTier = config.tiers.find((tier) => {
      return this.estimateBaseValue() <= tier.adjusted_upper_bound;
    });

    if (currentTier?.commission_percent) {
      this.estimatePercentage.set(currentTier.commission_percent);
    }

    log('   minValue', minValue);
    log('   maxValue', maxValue);
    const value = calculateEstimate(
      this.estimateBaseValue(),
      this.estimatePercentage()
    );
    log('   value', value);
    this.estimateComputedValue.set(value);
  }

  public setTiers() {
    const configs = this.estimateConfigurations();
    const config = configs ? configs[this.selectedPeriod()] : null;
    if (!config) {
      throw new Error('No config found');
    }

    log(
      'product-type-item.component.setTiers  ',
      this.selectedPeriod(),
      config
    );

    const percentages: number[] = [];

    // Find the positions for the hash marks on slider.
    if (config.tiers && config.tiers.length) {
      config.tiers.forEach((tier, index: number) => {
        const ratio = getSliderPosition(
          this.estimateMin(),
          this.estimateMax(),
          tier.adjusted_upper_bound
        );
        percentages.push(ratio);
      });
    }

    log('     percentages ', percentages);

    this.tiersPercentages.set(percentages);
  }

  public getUtilFillColor = (productType: ProductType) => {
    let color = 'darkred';
    const current = productType.physical_utilization_current
      ? productType.physical_utilization_current
      : 0;
    const bench = productType.physical_utilization_bench
      ? productType.physical_utilization_bench
      : 0;
    if (current >= bench) {
      color = 'green';
    }
    return color;
  };
}
